import { Utensils, ShoppingBag, Briefcase, Store, Factory, Building2, Rocket } from "lucide-react";

/**
 * Impact Meter — shows what a donor's amount unlocks for the community.
 * Tiers are cumulative: hitting $1000 lights up $25, $100, $500, and $1000.
 */
const TIERS = [
  { min: 25,     label: "$25",     Icon: Utensils,  title: "A Family Meal",             blurb: "Stocks a community pantry shelf and feeds a family of four for a week." },
  { min: 100,    label: "$100",    Icon: ShoppingBag, title: "One Member",              blurb: "Covers one Founder membership — a young entrepreneur gets a real seat at the table." },
  { min: 500,    label: "$500",    Icon: Briefcase, title: "A Legal Setup",             blurb: "Files an LLC, business license, and tax setup for a Black entrepreneur." },
  { min: 1000,   label: "$1K",     Icon: Store,     title: "A Storefront Grant",        blurb: "Underwrites first-month rent for a Black-owned corner store or salon." },
  { min: 5000,   label: "$5K",     Icon: Rocket,    title: "A Launch Kit",              blurb: "Full go-to-market kit: branding, inventory seed, and 3 months of insurance." },
  { min: 25000,  label: "$25K",    Icon: Building2, title: "A Franchise Unit",          blurb: "Seed capital toward opening a Black-owned grocery or restaurant franchise." },
  { min: 50000,  label: "$50K",    Icon: Factory,   title: "A Manufacturing Pilot",     blurb: "Funds a small Black-owned manufacturing pilot — real jobs, real ownership." },
];

export default function ImpactMeter({ amount }) {
  const numeric = Number(amount) || 0;
  const activeIdx = TIERS.findIndex((t, i) => {
    const next = TIERS[i + 1];
    return numeric >= t.min && (!next || numeric < next.min);
  });
  const highestUnlocked = TIERS.reduce((acc, t, i) => (numeric >= t.min ? i : acc), -1);

  // progress within the current tier -> next tier for the bar
  const cur = highestUnlocked >= 0 ? TIERS[highestUnlocked] : null;
  const next = highestUnlocked + 1 < TIERS.length ? TIERS[highestUnlocked + 1] : null;
  let pctToNext = 0;
  if (cur && next) {
    pctToNext = Math.min(100, Math.round(((numeric - cur.min) / (next.min - cur.min)) * 100));
  } else if (!cur && numeric > 0 && TIERS[0]) {
    pctToNext = Math.min(100, Math.round((numeric / TIERS[0].min) * 100));
  } else if (cur && !next) {
    pctToNext = 100;
  }

  const currentTierBlurb =
    highestUnlocked >= 0 ? TIERS[highestUnlocked] : null;
  const teaser = next
    ? `Add $${(next.min - numeric).toLocaleString()} to unlock "${next.title}"`
    : "You've unlocked every tier. This is legacy-level impact.";

  return (
    <div className="border border-bdu-gold/20 bg-bdu-surface p-6" data-testid="impact-meter">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="label-eyebrow">Your Impact</div>
        <div className="text-xs text-bdu-cream/50">
          {numeric > 0 ? <>at <span className="text-bdu-gold">${numeric.toLocaleString()}</span></> : "select an amount to see what you unlock"}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-5 h-1.5 bg-bdu-gold/10 relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-bdu-gold/80 to-bdu-gold transition-all duration-500"
          style={{ width: `${cur ? 100 : pctToNext}%` }}
          data-testid="impact-progress"
        />
        {cur && next && (
          <div
            className="absolute inset-y-0 left-0 bg-bdu-gold/50 transition-all duration-500"
            style={{ width: `${100 + pctToNext / TIERS.length}%` }}
          />
        )}
      </div>

      {/* Current unlock spotlight */}
      {currentTierBlurb ? (
        <div className="mt-5 flex items-start gap-4" data-testid="impact-current">
          <div className="shrink-0 w-12 h-12 border border-bdu-gold/50 bg-bdu-gold/10 flex items-center justify-center text-bdu-gold">
            <currentTierBlurb.Icon size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-xl text-bdu-cream leading-tight">
              {currentTierBlurb.title}
              <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-bdu-gold">unlocked</span>
            </div>
            <p className="mt-1 text-sm text-bdu-cream/70 leading-relaxed">{currentTierBlurb.blurb}</p>
          </div>
        </div>
      ) : (
        <div className="mt-5 text-sm text-bdu-cream/50 italic">
          Every dollar counts — even $25 feeds a family for a week.
        </div>
      )}

      {next && (
        <div className="mt-4 pl-16 text-xs text-bdu-gold/80" data-testid="impact-teaser">
          {teaser}
        </div>
      )}

      {/* Tier rail */}
      <div className="mt-8 grid grid-cols-7 gap-1 sm:gap-2" data-testid="impact-tier-rail">
        {TIERS.map((t, i) => {
          const unlocked = i <= highestUnlocked;
          const isCurrent = i === highestUnlocked;
          return (
            <div
              key={t.min}
              className={`flex flex-col items-center py-3 px-1 border transition-all ${
                unlocked
                  ? "border-bdu-gold/50 bg-bdu-gold/5"
                  : "border-bdu-gold/10 bg-transparent"
              } ${isCurrent ? "ring-1 ring-bdu-gold" : ""}`}
              title={`${t.label} · ${t.title}`}
              data-testid={`impact-tier-${t.min}`}
            >
              <t.Icon size={16} className={unlocked ? "text-bdu-gold" : "text-bdu-cream/25"} />
              <div className={`mt-1.5 text-[10px] font-semibold tracking-wider ${unlocked ? "text-bdu-gold" : "text-bdu-cream/30"}`}>
                {t.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
